/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ReactNode } from 'react';

import { EntityLoaders, LoadParams, useLoad } from 'entities';

import { AnyObject } from 'types';

import { LoadOptions, UseEntityLoad } from 'entities/hooks/load/createHook';

type EnforceProps<T extends AnyObject> = {
  [Key in keyof T]: NonNullable<T[Key]>;
};

type LoadTarget = keyof LoadParams;

type TargetParams<T> = T extends `${infer Module}.${infer Sub}`
  ? Module extends keyof EntityLoaders
    ? Sub extends keyof EntityLoaders[Module]
      ? LoadOptions<EntityLoaders, Module, Sub>
      : unknown
    : unknown
  : unknown;

type TargetResult<T> = T extends `${infer Module}.${infer Sub}`
  ? Module extends keyof EntityLoaders
    ? Sub extends keyof EntityLoaders[Module]
      ? UseEntityLoad<EntityLoaders, Module, Sub, any>
      : unknown
    : unknown
  : unknown;

type EntityLoaderProps<
  T extends LoadTarget,
  // SubType extends MessageType = MessageType,
> = TargetParams<T> & {
  target: T;

  defaultValue?: NonNullable<TargetResult<T>['data']>;

  children: (
    params: EnforceProps<Pick<TargetResult<T>, 'overwrite' | 'load' | 'retry' | 'data'>>,
  ) => ReactNode;

  loading?: ReactNode;

  error?: ReactNode | ((params: Pick<TargetResult<T>, 'load' | 'retry'>) => ReactNode);
};

export function EntityLoader<
  T extends LoadTarget,
  // SubType extends MessageType = MessageType,
>({ target, children, loading = null, error, ...hookProps }: EntityLoaderProps<T>) {
  const [mod, subMod] = target.split('.');

  // this is perfectly fine. The module.sub ref is protected by TS
  // @ts-ignore
  const { data, load, overwrite, retry, ...stateParams } = useLoad(mod, subMod, hookProps);

  if (stateParams.error)
    return (
      // @ts-ignore
      <>{error ? (typeof error === 'function' ? error?.({ load, retry }) : error) : loading}</>
    );

  if (stateParams.loading) return <>{loading}</>;

  return (
    <>
      {children({
        data: data as any,
        load,
        retry,
        overwrite,
      } as any)}
    </>
  );
}
